import type {
  PrescriptionFormatOverrides,
  PrescriptionFormatResponse,
  PrescriptionFormatSlotResponse,
  PrescriptionSectionKey,
  PrescriptionSlotKey,
} from './types';

/**
 * Pure helpers for reading a resolved prescription format (clinic-type preset + per-clinic overrides).
 *
 * Every label arrives as a translatable `labelKey` plus an optional clinic-authored `labelOverride`. The override
 * wins when present and is rendered verbatim — clinic-authored text cannot be translated, so it must never be fed
 * through `t()`.
 *
 * See .ai/BusinessDomain/visit-prescription.md → "Prescription Format".
 */

/** Resolves a label: the clinic's literal override if set, otherwise the translated preset key. */
export function resolveLabel(
  labelKey: string,
  labelOverride: string | null | undefined,
  t: (key: string) => string
): string {
  return labelOverride?.trim() ? labelOverride : t(labelKey);
}

/** The prescription section heading ("℞", "Treatment plan", or the clinic's own wording). */
export function resolveHeading(
  format: PrescriptionFormatResponse | undefined,
  t: (key: string) => string
): string {
  if (!format) return '';
  return resolveLabel(format.headingKey, format.headingOverride, t);
}

/** True when the format shows this section. An unknown/absent format shows everything rather than hiding data. */
export function isSectionVisible(
  format: PrescriptionFormatResponse | undefined,
  key: PrescriptionSectionKey
): boolean {
  if (!format) return true;
  return format.sections.some((section) => section.key === key);
}

/** The label for a section, or null when the format hides it. */
export function sectionLabel(
  format: PrescriptionFormatResponse | undefined,
  key: PrescriptionSectionKey,
  t: (key: string) => string,
  fallback: string
): string | null {
  if (!format) return fallback;
  const section = format.sections.find((candidate) => candidate.key === key);
  return section ? resolveLabel(section.labelKey, section.labelOverride, t) : null;
}

/** The visible item slots in entry/print order. Empty when the format has not loaded. */
export function visibleSlots(
  format: PrescriptionFormatResponse | undefined
): PrescriptionFormatSlotResponse[] {
  return format?.slots ?? [];
}

/** The visible slots other than the medicine/drug name, which the editor renders as its own primary field. */
export function detailSlots(
  format: PrescriptionFormatResponse | undefined
): PrescriptionFormatSlotResponse[] {
  return visibleSlots(format).filter((slot) => slot.key !== 'MedicineName');
}

export function findSlot(
  format: PrescriptionFormatResponse | undefined,
  key: PrescriptionSlotKey
): PrescriptionFormatSlotResponse | undefined {
  return format?.slots.find((slot) => slot.key === key);
}

/** True when the format shows this slot. An absent format shows everything. */
export function isSlotVisible(
  format: PrescriptionFormatResponse | undefined,
  key: PrescriptionSlotKey
): boolean {
  if (!format) return true;
  return format.slots.some((slot) => slot.key === key);
}

/** Maps a slot key to the matching `PrescriptionItem` field name on the API/draft shapes. */
export const SLOT_FIELD: Record<PrescriptionSlotKey, 'medicineName' | 'dose' | 'frequency' | 'duration' | 'instructions'> = {
  MedicineName: 'medicineName',
  Dose: 'dose',
  Frequency: 'frequency',
  Duration: 'duration',
  Instructions: 'instructions',
};

/**
 * Layers overrides onto a preset to produce an effective format — the client-side mirror of the backend's
 * PrescriptionFormatResolver, used ONLY by the clinic-settings live preview so unsaved edits are visible before
 * saving. Every render surface uses the server-resolved `layout.format` instead.
 *
 * Kept behaviourally identical to the backend: the prescription section and the medicine slot are never dropped, a
 * vocabulary override replaces rather than merges, and an empty vocabulary list means "no override".
 */
export function applyFormatOverrides(
  preset: PrescriptionFormatResponse,
  overrides: PrescriptionFormatOverrides
): PrescriptionFormatResponse {
  const hiddenSections = new Set(overrides.hiddenSections);
  const hiddenSlots = new Set(overrides.hiddenSlots);

  return {
    headingKey: preset.headingKey,
    headingOverride: overrides.rxHeading?.trim() ? overrides.rxHeading.trim() : null,
    sections: preset.sections
      .filter((section) => section.key === 'Prescription' || !hiddenSections.has(section.key))
      .map((section) => ({
        ...section,
        labelOverride: nonBlank(overrides.sectionLabels[section.key]),
      })),
    slots: preset.slots
      .filter((slot) => slot.key === 'MedicineName' || !hiddenSlots.has(slot.key))
      .map((slot) => {
        const vocabulary = overrides.slotVocabularies[slot.key];
        return {
          ...slot,
          labelOverride: nonBlank(overrides.slotLabels[slot.key]),
          vocabulary: vocabulary && vocabulary.length > 0 ? vocabulary : slot.vocabulary,
        };
      }),
  };
}

function nonBlank(value: string | undefined): string | null {
  return value?.trim() ? value.trim() : null;
}

/** The stored prescription-item fields a detail line can draw from. */
export interface PrescriptionItemValues {
  dose?: string | null;
  frequency?: string | null;
  duration?: string | null;
  instructions?: string | null;
}

/**
 * The non-blank detail values for one prescription item, in the order the clinic's format shows its slots and
 * excluding slots the format hides. `restrictTo` narrows it further for callers that render some slots separately
 * (e.g. instructions on its own line).
 */
export function itemDetailValues(
  item: PrescriptionItemValues,
  slots: PrescriptionFormatSlotResponse[],
  restrictTo?: PrescriptionSlotKey[]
): string[] {
  const allowed = restrictTo ? new Set<PrescriptionSlotKey>(restrictTo) : null;

  return slots
    .filter((slot) => !allowed || allowed.has(slot.key))
    .map((slot) => {
      switch (slot.key) {
        case 'Dose':
          return item.dose;
        case 'Frequency':
          return item.frequency;
        case 'Duration':
          return item.duration;
        case 'Instructions':
          return item.instructions;
        default:
          return null;
      }
    })
    .filter((value): value is string => Boolean(value && value.trim()));
}

/** The visit-workspace anchor id for each section, used by the section nav and scroll-to behaviour. */
export const SECTION_ANCHOR: Record<PrescriptionSectionKey, string> = {
  Vitals: 'vitals',
  Complaint: 'complaint',
  Diagnosis: 'diagnosis',
  Investigations: 'investigations',
  Prescription: 'rx',
  Advice: 'advice',
  FollowUp: 'followup',
};

/** The default section order, used until the clinic's format has loaded. */
export const DEFAULT_SECTION_ORDER: PrescriptionSectionKey[] = [
  'Vitals',
  'Complaint',
  'Diagnosis',
  'Investigations',
  'Prescription',
  'Advice',
  'FollowUp',
];

export interface FormatSectionView {
  key: PrescriptionSectionKey;
  /** Anchor id for the workspace section nav. */
  id: string;
  label: string;
  /** Display number ("01", "02", …), sequential over visible sections only. */
  number: string;
}

/**
 * The ordered, visible sections for the editable workspace, with resolved labels and gap-free numbering. Falls back
 * to the full default order while the format loads, so the workspace never renders an empty paper.
 */
export function formatSections(
  format: PrescriptionFormatResponse | undefined,
  t: (key: string) => string,
  fallbackLabel: (key: PrescriptionSectionKey) => string
): FormatSectionView[] {
  const entries = format
    ? format.sections.map((section) => ({
        key: section.key,
        label: resolveLabel(section.labelKey, section.labelOverride, t),
      }))
    : DEFAULT_SECTION_ORDER.map((key) => ({ key, label: fallbackLabel(key) }));

  return entries.map((entry, index) => ({
    key: entry.key,
    id: SECTION_ANCHOR[entry.key],
    label: entry.label,
    number: String(index + 1).padStart(2, '0'),
  }));
}

/**
 * Section numbers shown on the paper ("01", "02", …). Numbered over the *visible* sections so hiding one never
 * leaves a gap in the printed sequence.
 */
export function sectionNumbers(
  format: PrescriptionFormatResponse | undefined,
  fallbackOrder: PrescriptionSectionKey[]
): Record<string, string> {
  const keys = format ? format.sections.map((section) => section.key) : fallbackOrder;
  return keys.reduce<Record<string, string>>((numbers, key, index) => {
    numbers[key] = String(index + 1).padStart(2, '0');
    return numbers;
  }, {});
}
